import { useMemo } from "react";
import {
  CONNECTOR_STATUS_POLL_MS,
  useConnectorStatus,
  type ConnectorIssueReason,
} from "./useConnectorStatus.js";

/**
 * Lightweight workspace-wide "a connected service needs attention" signal.
 *
 * A cheap selector over the shared `useConnectorStatus` poll (the same
 * `GET /api/settings/connector-status` health feed the Services view uses),
 * distilling it to the connectors in an `error` state — most often a dead Google
 * grant ("Google sign-in expired — reconnect required") surfaced by the
 * background calendar-brief lane. The shell footer uses this to light a small
 * reconnect dot next to the account name and the Integrations menu item, so a
 * revoked grant is visible without opening Settings.
 *
 * The fetch / interval / window-focus refetch / cancelled-guard / error-swallow
 * lifecycle lives once in useConnectorStatus.
 */

/** Re-exported so existing consumers (FrontConnectorHealth) keep their import. */
export type { ConnectorIssueReason } from "./useConnectorStatus.js";

export interface ConnectorAlert {
  /** Canonical source key, e.g. "gcal", "gmail", "notion". */
  source: string;
  /** Human-readable connector label, e.g. "Google Calendar". */
  label: string;
  /** Sanitized, token-free reason, e.g. "Google sign-in expired — reconnect required". */
  message: string;
  /** Precise reason code, when the backend supplies one — drives tuned copy/CTA. */
  reason?: ConnectorIssueReason;
}

export interface ConnectorAlertsState {
  alerts: ConnectorAlert[];
  hasAlerts: boolean;
}

export function useConnectorAlerts(
  pollIntervalMs: number = CONNECTOR_STATUS_POLL_MS,
): ConnectorAlertsState {
  const rows = useConnectorStatus(pollIntervalMs);
  const alerts = useMemo(
    () =>
      rows
        .filter((row) => row.state === "error")
        .map((row) => ({
          source: row.source,
          label: row.label,
          message: row.lastError?.message ?? "Needs attention",
          ...(row.reason ? { reason: row.reason } : {}),
        })),
    [rows],
  );
  return { alerts, hasAlerts: alerts.length > 0 };
}
